import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useEventSalesStatus } from '@hooks/useEventSalesStatus'
import { formatShortDate, formatTime, formatCurrency, parseDate } from '@utils/formatters'
import { CITIES } from '@utils/geolocation'
import Badge from '@components/Badge'
import { DISABLE_LAST_UNITS_BADGE } from '@config/features'
import './EventCard.css'

const EventCard = ({ 
  event, 
  variant = 'default'
}) => {
  const {
    event_id,
    name,
    start_date,
    date,
    unit_id,
    main_image_url, 
    sale_status, 
    total_stock,
    sold_stock,
    ticket_types,
    sales_start_at
  } = event

  const displayDate = start_date || date; 

  // Determine location based on custom location or unit_id
  const citySlug = Object.keys(CITIES).find(key => CITIES[key].unit_id === unit_id);
  const locationName = event.location_name || (citySlug ? `Arq ${CITIES[citySlug].name}` : 'Local não informado');


  const isSoldOut = sold_stock >= total_stock
  const isAlmostSoldOut = sold_stock >= (total_stock * 0.9) && !isSoldOut

  // Calculate minPrice from ticket_types
  const minPrice = ticket_types && ticket_types.length > 0
    ? Math.min(...ticket_types.map(ticket => ticket.value_cents)) / 100
    : undefined;

  const { isSalesNotStarted, isEventEnded: isEnded } = useEventSalesStatus(event);

  const salesOpeningFormatted = useMemo(() => {
    if (!sales_start_at) return null;
    const shortDate = formatShortDate(sales_start_at);
    const time = formatTime(sales_start_at);
    return shortDate && time ? `${shortDate} às ${time}` : shortDate || time;
  }, [sales_start_at]);

  const cardContent = (
    <>
      <div className="event-card__image-container">
        <img 
          src={main_image_url || '/placeholder-event.jpg'} 
          alt={name}
          className="event-card__image"
          loading="lazy"
          decoding="async"
        />
        
        {/* Sales Opening Badge */}
        {!isEnded && isSalesNotStarted && (
          <div className="event-card__sales-badge">
            <div className="event-card__sales-badge-icon">
              <svg 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div className="event-card__sales-badge-content">
              <span className="event-card__sales-badge-label">Abertura de vendas</span>
              <span className="event-card__sales-badge-date">
                {salesOpeningFormatted || 'Em breve'}
              </span>
            </div>
          </div>
        )}

        {/* Badges */}
        <div className="event-card__badges">
          {isEnded && (
            <Badge variant="secondary">Encerrado</Badge>
          )}
          {!isEnded && !isSalesNotStarted && isSoldOut && (
            <Badge variant="danger">Esgotado</Badge>
          )}
          {!isEnded && !isSalesNotStarted && isAlmostSoldOut && !DISABLE_LAST_UNITS_BADGE && (
            <Badge variant="warning">Últimos ingressos</Badge>
          )}
        </div>
      </div>

      <div className="event-card__content">
        <h3 className="event-card__title">{name}</h3>
        
        <div className="event-card__info">
          <span className="event-card__date">
            {formatShortDate(displayDate)} às {formatTime(displayDate)}
          </span>
          <span className="event-card__location">{locationName}</span>
        </div>

        {!isEnded && !isSalesNotStarted && minPrice !== undefined ? (
          <div className="event-card__price">
            <span className="event-card__price-label">A partir de</span>
            <span className="event-card__price-value">{formatCurrency(minPrice)}</span>
          </div>
        ) : null}
      </div>
    </>
  )

  return isEnded ? (
    <div className={`event-card event-card--${variant} ${isEnded ? 'event-card--ended' : ''}`}>
      {cardContent}
    </div>
  ) : (
    <Link 
      to={`/${CITIES[citySlug]?.slug}/evento/${event_id}`} 
      className={`event-card event-card--${variant} ${isEnded ? 'event-card--ended' : ''}`}
    >
      {cardContent}
    </Link>
  )
}

export default EventCard
